/**
 * Playlist Sync Provider Interface
 *
 * Defines a generic interface for syncing songs with external music service playlists.
 * Implementations handle the specifics of each music service (Spotify, Apple Music, etc).
 */

import type { MusicService } from '../../types/domain';
import type { UserSettings } from '../../types/database';
import type { PlaylistSyncResult, PlaylistSyncConfig } from './types';

/**
 * Generic interface for playlist sync operations.
 *
 * This allows the app to sync songs with any music service by implementing
 * this interface. Each implementation handles service-specific details.
 */
export interface PlaylistSyncProvider {
  /**
   * The music service this provider syncs with.
   */
  readonly serviceId: MusicService;

  /**
   * Display name for the service.
   */
  readonly displayName: string;

  /**
   * Add a track to the user's synced playlist.
   *
   * @param trackId - Music service track ID (e.g., Spotify track ID)
   * @param playlistId - Target playlist ID
   * @param accessToken - Valid access token for the music service
   * @returns Result of the operation
   */
  addTrackToPlaylist(
    trackId: string,
    playlistId: string,
    accessToken: string
  ): Promise<PlaylistSyncResult>;

  /**
   * Remove a track from the user's synced playlist.
   *
   * @param trackId - Music service track ID
   * @param playlistId - Target playlist ID
   * @param accessToken - Valid access token for the music service
   * @returns Result of the operation
   */
  removeTrackFromPlaylist(
    trackId: string,
    playlistId: string,
    accessToken: string
  ): Promise<PlaylistSyncResult>;

  /**
   * Refresh an expired access token.
   *
   * @param refreshToken - Refresh token from previous token response
   * @returns New access token and expiration, or error
   */
  refreshAccessToken(refreshToken: string): Promise<{
    success: boolean;
    accessToken?: string;
    expiresAt?: string;
    refreshToken?: string;
    error?: string;
  }>;

  /**
   * Check if a token is expired or about to expire.
   *
   * @param expiresAt - Token expiration timestamp (ISO 8601)
   * @param bufferMs - Buffer time in milliseconds (default 5 minutes)
   * @returns True if token is expired or will expire within buffer
   */
  isTokenExpired(expiresAt: string | null, bufferMs?: number): boolean;
}

/**
 * Factory function type for creating playlist sync providers.
 */
export type PlaylistSyncProviderFactory = () => PlaylistSyncProvider;

/**
 * Get the user's current playlist sync configuration.
 *
 * Note: Tokens are NOT returned here. They are handled securely server-side
 * by the spotify-playlist-sync edge function and never sent to the client.
 *
 * @param userId - User ID to get config for
 * @returns The user's playlist sync configuration (without tokens)
 */
export async function getPlaylistSyncConfig(userId: string): Promise<PlaylistSyncConfig> {
  // Import here to avoid circular dependency
  const { supabase } = await import('../supabase');

  type UserSettingsRow = Pick<
    UserSettings,
    | 'music_service'
    | 'music_service_playlist_id'
    | 'remove_from_playlist_on_delete'
  >;

  const { data, error } = await supabase
    .from('user_settings')
    .select('music_service, music_service_playlist_id, remove_from_playlist_on_delete')
    .eq('user_id', userId)
    .maybeSingle();

  if (error || !data) {
    return {
      isConnected: false,
      service: null,
      playlistId: null,
      accessToken: null,
      refreshToken: null,
      tokenExpiresAt: null,
      removeFromPlaylistOnDelete: false,
    };
  }

  const settings = data as unknown as UserSettingsRow;

  // Check if user has a connected music service with a playlist
  const isConnected = !!settings.music_service && !!settings.music_service_playlist_id;

  return {
    isConnected,
    service: settings.music_service,
    playlistId: settings.music_service_playlist_id,
    // Tokens are handled server-side, never sent to client
    accessToken: null,
    refreshToken: null,
    tokenExpiresAt: null,
    removeFromPlaylistOnDelete: settings.remove_from_playlist_on_delete ?? false,
  };
}
